import { TRACKS_HIDE } from '@/constants';
import download from '@/utils/download';
import FeedbackProxy from '@/proxies/Feedback';
import JerseysProxy from '@/proxies/Jerseys';
import JerseysTransform from '@/transforms/Jerseys';
import ProjectsProxy from '@/proxies/Projects';
import TracksTransform from '@/transforms/TracksMapping';
import { getReviewColor } from '@/transforms/TracksMapping';
import * as types from './mutation-types';

export default {
  async addJerseyFeedback({ dispatch }, payload) {
    const proxy = new FeedbackProxy();
    try {
      await proxy.addJerseyFeedback(payload);
      dispatch('notification/success', 'confirmationmessage.add_feedback', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_add_feedback', { root: true });
    }
  },

  async addTrackFeedback({ dispatch }, payload) {
    const proxy = new FeedbackProxy();
    try {
      await proxy.addTrackFeedback(payload);
      dispatch('notification/success', 'confirmationmessage.add_feedback', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_add_feedback', { root: true });
    }
  },

  async downloadTracks({ dispatch, rootGetters }, payload) {
    const { job_id, tracks_ids } = payload;
    try {
      const url = `${rootGetters.baseUrlPath}/api/v1/tracks/${job_id}?tracks_ids=${tracks_ids}`;
      download(url);
      dispatch('notification/success', 'confirmationmessage.download_starts_soon', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_download', { root: true });
    }
  },

  async getCanonicalPoints({ commit, dispatch, getters }, payload) {
    commit(types.LOADING, true);
    try {
      const proxy = new ProjectsProxy();
      const { tracks } = await proxy.getCanonicalPoints(payload);
      const responseFormat = tracks.reduce((res, cur) => {
        res[cur.track_id] = TracksTransform.canonicalPoints(cur.points);
        return res;
      }, {});
      const result = {
        ...getters.canonicalPoints,
        ...responseFormat,
      }
      commit(types.SET_CANONICAL_POINTS, result);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_canonical_points', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async getItems({ commit, dispatch, getters }, payload) {
    commit(types.LOADING, true);
    const proxy = new ProjectsProxy();
    try {
      const response = await proxy.getTracksMapping(payload);
      commit(types.TRACKS_MAPPING, TracksTransform.fetchCollection(response.tracks_mapping).filter(item => !!item));
      commit(types.MORE, response.total > getters.items.length);
      commit(types.TOTAL, response.total);
    } catch (e) {
      dispatch('notification/error', ['errormessage.failed_to_get_tracks_mapping', { job_id: payload }] , { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  changeTrack({ commit, getters }, payload) {
    const result = getters.items.map(item => {
      const { id, player = {} } = payload;
      if (id === item.id) {
        return {
          ...item,
          ...payload,
          player: {
            ...item.player,
            ...player,
          }
        }
      } else {
        return item;
      }
    });
    commit(types.TRACKS_MAPPING, result);
  },

  async getJerseysOverview({ commit, getters }, payload = {}) {
    try {
      const proxy = new JerseysProxy();
      const { job_id, track_id } = payload;
      const { detected_jerseys } = await proxy.getJerseysFastOverview(job_id, track_id);
      const items = JerseysTransform.fetchCollection(detected_jerseys);
      commit(types.SET_JERSEYS_OVERVIEW, {
        ...getters.jerseysOverview,
        [track_id]: items,
      });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_jerseys', { root: true });
    }
  },

  async markAsFinishedTracks({ commit, dispatch, getters }, payload) {
    const proxy = new ProjectsProxy();
    commit(types.LOADING, true);
    try {
      await proxy.markAsFinishedTracks(payload);
      const tracks = getters.items.map(el => {
        const isUpdatedTrack = payload.tracks.find(({ track_id }) => track_id === el.track_id);
        if (isUpdatedTrack) {
          return {
            ...el,
            color: getReviewColor(isUpdatedTrack.review_status),
            review_status: isUpdatedTrack.review_status,
          }
        }
        return el;
      })
      commit(types.TRACKS_MAPPING, tracks);
      dispatch('notification/success', 'confirmationmessage.tracks_marked_as_finished', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_mark_finish_tracks', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  setFilterHide({ commit, getters }, payload) {
    const result = {
      ...getters.filters,
      [TRACKS_HIDE]: payload.length ? [...getters.filters[TRACKS_HIDE], ...payload] : [],
    };

    commit(types.SET_FILTERES, result);
  },

  setFilters({ commit, getters }, payload) {
    const { type, value } = payload;
    const result = {
      ...getters.filters,
      [type]: value,
    };

    commit(types.SET_FILTERES, result);
  },

  async updateTracks({ commit, dispatch }, payload) {
    try {
      commit(types.LOADING, true);
      await Promise.all(payload.map(el => {
        const proxy = new ProjectsProxy();
        return proxy.updateMapping(el);
      }));
      await dispatch('refresh', payload[0].job_id);
      dispatch('notification/success', 'confirmationmessage.track_mapping_updated', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_update_track_mapping', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async updateTracksMapping({ commit, dispatch, getters }, payload) {
    const { player, track_id } = payload;
    const { notificationPosition, ...options } = payload;
    const proxy = new ProjectsProxy();
    commit(types.LOADING, true);

    try {
      const response = await proxy.updateMapping(options);
      const result = getters.items.map(el => {
        if (el.track_id === track_id) {
          return TracksTransform.updateItem(el, response);
        }
        return el;
      });

      commit(types.TRACKS_MAPPING, result);
      const message = 'confirmationmessage.track_mapping_updated';
      if (notificationPosition) {
        const notification = {
          message,
          position: notificationPosition,
        };
        dispatch('notification/success', notification, { root: true });
      } else {
        dispatch('notification/success', message, { root: true });
      }
      dispatch('project/changeOneDetect', { player, track_id }, { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_update_track_mapping', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async deleteItem({ commit, dispatch, rootGetters, state }, payload) {
    const proxy = new ProjectsProxy();
    // commit(types.LOADING, true);
    try {
      await proxy.deleteMappings(payload);
      await dispatch('project/getObjects', {
        frame_id: rootGetters['project/currentFrame'],
        job_id: payload.job_id,
      }, { root: true });
      commit(types.DELETE, payload);
      const deletionCount = Array.isArray(payload.tracks_ids) ? payload.tracks_ids.length : 1;
      commit(types.TOTAL, state.total - deletionCount);
      dispatch('notification/success', ['confirmationmessage.tracks_deleted', { total: deletionCount }], { root: true });
    } catch (e) {
      dispatch('notification/error', `errormessage.failed_to_delete_track_mapping`, { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async joinTracks({ commit, dispatch }, payload) {
    const proxy = new ProjectsProxy();
    commit(types.LOADING, true);
    try {
      await proxy.joinTracks(payload);
      await dispatch('refresh', payload.job_id);
      dispatch('notification/success', 'confirmationmessage.tracks_joined', { root: true });
    } catch (e) {
      if (Object.prototype.hasOwnProperty.call(e.data, 'exception')) {
        dispatch('notification/error', 'errormessage.error_overlapping_tracks', { root: true });
      } else {
        dispatch('notification/error', 'errormessage.failed_to_join_tracks', { root: true });
      }
    } finally {
      commit(types.LOADING, false);
    }
  },

  async splitTrack({ commit, dispatch }, payload) {
    const proxy = new ProjectsProxy();
    commit(types.LOADING, true);
    try {
      await proxy.splitTrack(payload);
      await dispatch('refresh', payload.job_id);
      dispatch('notification/success', 'confirmationmessage.track_split', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_split_track', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async splitTrackByJersey({ commit, dispatch, getters }, payload) {
    try {
      dispatch('jerseys/setLoading', true, { root: true });
      const proxy = new ProjectsProxy();
      const { total, tracks_mapping } = await proxy.splitTrackByJersey(payload.job_id, payload.track_id);

      commit(types.TRACKS_MAPPING, TracksTransform.fetchCollection(tracks_mapping).filter(item => !!item));
      commit(types.MORE, total > getters.items.length);
      commit(types.TOTAL, total);
      dispatch('notification/success', 'confirmationmessage.track_split_by_jersey', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_split_track_by_jersey', { root: true });
    } finally {
      dispatch('jerseys/setLoading', false, { root: true });
    }
  },

  async swapTrack({ commit, dispatch }, payload) {
    const proxy = new ProjectsProxy();
    commit(types.LOADING, true);
    try {
      await proxy.swapTrack(payload);
      await dispatch('refresh', payload.job_id);
      dispatch('notification/success', 'confirmationmessage.tracks_swap', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_swap_tracks', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  reset({ commit }) {
    commit(types.RESET);
  },

  sortItems({ commit, dispatch }, payload) {
    commit(types.SORT, payload);
    dispatch('getItems');
  },

  async refresh({ commit, dispatch, getters }, payload) {
    const proxy = new ProjectsProxy();
    try {
      const response = await proxy.getTracksMapping(payload);
      commit(types.TRACKS_MAPPING, TracksTransform.fetchCollection(response.tracks_mapping).filter(item => !!item));
      commit(types.MORE, response.total > getters.items.length);
      commit(types.TOTAL, response.total);
    } catch (e) {
      dispatch('notification/error', ['errormessage.failed_to_get_tracks_mapping', { job_id: payload }], { root: true });
    }
  },

  async refreshTracks({ commit, dispatch }, payload) {
    try {
      commit(types.LOADING, true);
      await dispatch('refresh', payload);
      dispatch('notification/success', 'confirmationmessage.tracks_refreshed', { root: true });
    } catch (e) {
      dispatch('notification/error', ['errormessage.failed_to_refresh_tracks_mapping', { job_id: payload }], { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  resetModal({ commit }) {
    commit(types.RESET_MODAL);
  },

  setModal({ commit }, payload) {
    commit(types.SET_MODAL, payload);
  },

  setTableMode({ commit }, payload) {
    commit(types.SET_TABLE_MODE, payload);
  },

  async runInterpolationForTracks({ commit, dispatch }, payload) {
    try {
      commit(types.LOADING, true);
      const proxy = new ProjectsProxy();
      await proxy.runInterpolationForTracks(payload);
      dispatch('notification/success', 'confirmationmessage.interpolation_started', { root: true });
    } catch (e) {
      dispatch('notification/error', `errormessage.failed_to_start_interpolation`, { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async runRebuildPostTrackingTeam({ commit, dispatch }, payload) {
    try {
      commit(types.LOADING, true);
      const proxy = new ProjectsProxy();
      await proxy.runRebuildPostTrackingTeam(payload.job_id, payload.team_id);
      const jobName = await dispatch('getJobName', payload.job_id);
      dispatch('notification/success', ['confirmationmessage.rebuild_post_tracking', { jobName }], { root: true });
    } catch (e) {
      dispatch('notification/error', `errormessage.failed_to_rebuild_post_tracking`, { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async runRebuildPostTrackingPlayer({commit, dispatch}, payload) {
    try {
      commit(types.LOADING, true);
      const proxy = new ProjectsProxy();
      await proxy.runRebuildPostTrackingPlayer(payload);
      const jobName = await dispatch('getJobName', payload.job_id);
      dispatch('notification/success', ['confirmationmessage.rebuild_post_tracking', { jobName }], { root: true });
    } catch (e) {
      dispatch('notification/error', `errormessage.failed_to_rebuild_post_tracking`, { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },

  async trimTrack({ commit, dispatch }, payload) {
    try {
      commit(types.LOADING, true);
      const proxy = new ProjectsProxy();
      await proxy.trimTrack(payload);
      await dispatch('refresh', payload.job_id);
      dispatch('notification/success', 'confirmationmessage.track_trimmed', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_trim_track', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },
};
